import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { IconButton, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageContent } from "../components/PageContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { CatalogListName } from "../components/CatalogOriginChip";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useShowCatalogOriginMarkers } from "../hooks/useShowCatalogOriginMarkers";
import { CATALOG_NEW_ITEM_ID } from "./catalog/catalogNav";
import {
  useGetModifiersQuery,
  useDeleteModifierMutation,
} from "../query/modifiersQuery";
import { formatMoney } from "../utils/formatMoney";
import type { ModifierId } from "@KassaroAB/shared/types/ids";

export const ModifiersView = () => {
  const { t } = useTranslation();
  const { scope, pagePath } = useCatalogScope("modifiers");
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);

  const [deleteId, setDeleteId] = useState<ModifierId | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: modifiers,
    isLoading,
    error: loadError,
  } = useGetModifiersQuery(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteModifierMutation(scope);

  const modifierList = modifiers ?? [];
  const deleteName =
    modifierList.find((modifier) => modifier.id === deleteId)?.name ?? "";

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteMutation(deleteId, {
      onSuccess: () => {
        setSuccessMessage(t("modifiersView.successDeleted"));
        setDeleteId(null);
      },
      onError: () => setSuccessMessage(null),
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isLoading && modifierList.length === 0) {
    return (
      <PageContent title={t("modifiersView.title")}>
        <LoadingContainer message={t("modifiersView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("modifiersView.title")}
      actions={
        <RouterButtonLink to={`${pagePath}/${CATALOG_NEW_ITEM_ID}`}>
          <Plus size={16} aria-hidden />
          {t("modifiersView.create")}
        </RouterButtonLink>
      }
    >
      {loadError && (
        <InfoBox variant="error">{t("modifiersView.errorLoad")}</InfoBox>
      )}
      {deleteError && (
        <InfoBox variant="error">{t("modifiersView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      {modifierList.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>
                  {t("modifiersView.tableName")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("modifiersView.tablePrice")}
                </TableHeaderCell>
                <TableHeaderCell>{t("modifiersView.tableVat")}</TableHeaderCell>
                <TableHeaderCell align="right">{null}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {modifierList.map((modifier) => (
                <TableRow key={modifier.id} to={`${pagePath}/${modifier.id}`}>
                  <TableCell noWrap>
                    <CatalogListName
                      name={modifier.name}
                      origin={modifier.origin}
                      showOrigin={showCatalogOrigin}
                    />
                  </TableCell>
                  <TableCell noWrap>{formatMoney(modifier.price)}</TableCell>
                  <TableCell noWrap>
                    {modifier.VAT != null ? `${modifier.VAT}%` : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("common.delete")}
                      disabled={isDeletePending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteId(modifier.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState
            message={`${t("modifiersView.empty")} ${t("modifiersView.emptyHint")}`}
          />
        </Card>
      )}
      <ConfirmDialog
        isOpen={deleteId != null}
        title={t("modifierForm.deleteModifier")}
        message={t("modifiersView.deleteConfirm", { name: deleteName })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContent>
  );
};
